import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, User, Heart, Menu, X } from "lucide-react";
import { useState } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useSiteInfo } from "../context/SiteInfoContext";

const NAV = [
  { to: "/shop", label: "Shop All" },
  { to: "/category/rings", label: "Rings" },
  { to: "/category/necklaces", label: "Necklaces" },
  { to: "/category/earrings", label: "Earrings" },
  { to: "/category/bracelets", label: "Bracelets" },
  { to: "/category/others", label: "Others" },
  { to: "/journal", label: "Journal" },
];

const AUDIENCE = [
  { to: "/shop?audience=women", label: "Women" },
  { to: "/shop?audience=men", label: "Men" },
  { to: "/shop?audience=children", label: "Children" },
];

export default function Navbar() {
  const { count, setDrawerOpen, wishlist } = useCart();
  const { user } = useAuth();
  const { info } = useSiteInfo();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false); setOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-hair" data-testid="navbar">
      {/* Announcement strip with audience pills */}
      <div className="bg-[var(--bg-tertiary)] text-[11px] tracking-[0.22em] uppercase text-neutral-700 px-4 md:px-10 py-2.5 flex flex-col sm:flex-row gap-2 sm:gap-0 items-center justify-between">
        <span className="text-center sm:text-left">{info.announcement}</span>
        <div className="flex gap-5" data-testid="audience-strip">
          {AUDIENCE.map((a) => (
            <Link key={a.to} to={a.to} data-testid={`audience-${a.label.toLowerCase()}`}
              className="hover:text-[var(--gold)] transition-colors">
              {a.label}
            </Link>
          ))}
        </div>
      </div>

      <div className="max-w-[1480px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-6">
        <button className="lg:hidden" data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X strokeWidth={1.25} /> : <Menu strokeWidth={1.25} />}
        </button>

        <Link to="/" className="font-serif text-2xl tracking-[0.32em] uppercase flex items-center gap-2" data-testid="brand-logo">
          {info.logo_url ? (
            <img src={info.logo_url} alt={info.brand_name} className="h-10 w-auto object-contain" />
          ) : (info.brand_name || "").includes("·") ? (
            <>{info.brand_name.split("·")[0]}<span className="gold">·</span>{info.brand_name.split("·")[1]}</>
          ) : (
            <span>{info.brand_name || "Aura"}</span>
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-8 text-[12px] tracking-[0.18em] uppercase">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}
              className={({ isActive }) => `transition-colors ${isActive ? "text-[var(--gold)]" : "text-neutral-800 hover:text-[var(--gold)]"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <button onClick={() => setSearchOpen(!searchOpen)} aria-label="Search" data-testid="search-toggle">
            <Search strokeWidth={1.25} className="w-5 h-5" />
          </button>
          <Link to="/account?tab=wishlist" className="hidden md:inline-flex relative" data-testid="wishlist-link" aria-label="Wishlist">
            <Heart strokeWidth={1.25} className="w-5 h-5" />
            {wishlist.length > 0 && <span className="absolute -top-1 -right-2 text-[10px] bg-[var(--gold)] text-white px-1">{wishlist.length}</span>}
          </Link>
          <Link to={user ? "/account" : "/login"} aria-label="Account" data-testid="account-link">
            <User strokeWidth={1.25} className="w-5 h-5" />
          </Link>
          <button onClick={() => setDrawerOpen(true)} className="relative" data-testid="cart-btn" aria-label="Cart">
            <ShoppingBag strokeWidth={1.25} className="w-5 h-5" />
            {count > 0 && <span className="absolute -top-1 -right-2 text-[10px] bg-[var(--gold)] text-white px-1" data-testid="cart-count">{count}</span>}
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-hair bg-white" data-testid="search-bar">
          <form onSubmit={submit} className="max-w-[1480px] mx-auto px-6 md:px-10 py-5 flex items-center gap-4">
            <Search strokeWidth={1.25} className="w-5 h-5 text-neutral-500" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search rings, necklaces, materials…"
              data-testid="search-input"
              className="flex-1 bg-transparent border-none outline-none text-base font-light" />
            <button type="submit" className="btn-ghost" data-testid="search-submit">Search</button>
          </form>
        </div>
      )}

      {open && (
        <div className="lg:hidden border-t border-hair bg-white" data-testid="mobile-nav">
          <div className="px-6 py-6 flex flex-col gap-4">
            <p className="eyebrow text-neutral-500">Shop by</p>
            {AUDIENCE.map((a) => (
              <Link key={a.to} to={a.to} onClick={() => setOpen(false)}
                className="text-[13px] tracking-[0.18em] uppercase text-[var(--gold)]">{a.label}</Link>
            ))}
            <div className="rule my-2" />
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                className="text-[13px] tracking-[0.18em] uppercase">{n.label}</Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
